'use client';
import EmailField from '@/components/form/lib/EmailField/EmailField';
import PasswordField from '@/components/form/lib/PasswordField/PasswordField';
import InputField from '@/components/form/lib/InputField/InputField';
import MaskedField from '@/components/form/lib/InputField/MaskedField';
import CheckboxField from '@/components/form/lib/InputField/CheckboxField';
import Text from '@/components/common/Text/Text';

import type { ReferralCopyProps } from '@/components/common/SignUpApp/SignUpApp';
import ReferralCodeField from './ReferralCodeField';

type ApplicationInfoFieldsProps = {
  referralCopy?: ReferralCopyProps;
  onReferralCommit: (referralCode: string) => void;
  /** The referral code the feedback describes — see `ReferralCodeField`. */
  appliedReferralCode?: string;
};

const PHONE_MASK = [
  '(',
  /[1-9]/,
  /\d/,
  /\d/,
  ')',
  ' ',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/
];

/**
 * Same digit-range caps as the legacy multi-page flow's birthday mask (month
 * starts 0-1, day starts 0-3, year starts 1-2): it does not fully validate a
 * date as you type — `isValidBirthday` still does that on blur — but it
 * blocks the most obviously-wrong keystrokes (a month starting with 9, a
 * year starting with 0) the moment you'd type them, matching what the rest
 * of the app already does here.
 */
const BIRTHDAY_MASK = [
  /[0-1]/,
  /[0-9]/,
  '/',
  /[0-3]/,
  /[0-9]/,
  '/',
  /[1-2]/,
  /\d/,
  /\d/,
  /\d/
];

/**
 * "My Info" — the merge of the multi-page flow's step 1 (account) and
 * step 2 (personalize).
 */
function ApplicationInfoFields(props: ApplicationInfoFieldsProps) {
  const { referralCopy, onReferralCommit, appliedReferralCode } = props;

  return (
    <div className='ApplicationInfoFields'>
      <div className='Signup-row'>
        <InputField
          className='Signup-first-name'
          name='firstName'
          label='First Name'
          type='text'
          autoComplete='given-name'
          data-testid='signup-first-name'
        />
        <InputField
          className='Signup-last-name'
          name='lastName'
          label='Last Name'
          type='text'
          autoComplete='family-name'
          data-testid='signup-last-name'
        />
      </div>

      <EmailField
        className='Signup-email'
        name='email'
        label='Email'
        autoComplete='email'
        data-testid='signup-email'
      />

      <div className='Signup-row'>
        <MaskedField
          className='Signup-phone'
          mask={PHONE_MASK}
          label='Phone Number'
          name='phone'
          guide={false}
          autoComplete='tel'
          placeholder='(xxx) xxx-xxxx'
          data-testid='signup-phone'
        />
        <PasswordField
          name='password'
          label='Create a Password'
          autoComplete='new-password'
          data-testid='signup-password'
        />
      </div>

      <div className='Signup-row'>
        <ReferralCodeField
          className='Signup-referral'
          referralCopy={referralCopy}
          appliedCode={appliedReferralCode}
          onCommit={onReferralCommit}
          data-testid='signup-referral-code'
        />
        <MaskedField
          className='Signup-birthday'
          guide={false}
          mask={BIRTHDAY_MASK}
          label='Birthday'
          name='birthday'
          placeholder='mm/dd/yyyy'
          autoComplete='bday'
          data-testid='signup-birthday'
        />
      </div>

      <div className='Signup-row'>
        <InputField
          className='Signup-instagram'
          name='instagram'
          label='Instagram Handle (optional)'
          data-testid='signup-instagram'
        />
        <InputField
          className='Signup-tiktok'
          name='tiktok'
          label='TikTok Handle (optional)'
          data-testid='signup-tiktok'
        />
      </div>

      <div className='Signup-optins'>
        <CheckboxField
          name='optinPromotion'
          className='Signup-promotional-optin'
          label='(Optional) Sign me up for Vivrelle promotional emails.'
        />
        <CheckboxField
          name='optinText'
          className='Signup-text-optin'
          label={
            <span>
              <Text variant='span-s'>
                (Optional){' '}
                <Text variant='span-s-500'>Get notified when approved</Text>{' '}
                plus exclusive discounts and cart reminders from Vivrelle via
                text. Msg &amp; Data rates may apply. Msg frequency may vary.
                Reply HELP for help. Reply STOP to opt-out.
              </Text>{' '}
              <a
                href='/terms'
                className='Signup-terms'
                target='_blank'
              >
                Terms &amp; Conditions.
              </a>
            </span>
          }
        />
      </div>
    </div>
  );
}

export default ApplicationInfoFields;
